import React from 'react'
import {
  StyleProp,
  View,
  ViewStyle,
  Text,
  StyleSheet,
  TextStyle,
} from 'react-native'
import { OtherInvoiceInfo_v2 } from '../../InvoiceDetails/components/OtherInvoiceInfo'
import { CompanyHeader_v2 } from '../../InvoiceDetails/components/CompanyHeader'
import { Divider } from 'react-native-paper'
import Button from './Button'

interface AdditionalInvoiceFieldProps {
  additionalFields: Array<{
    label: string
    value: string | number | JSX.Element
    valueStyle?: TextStyle
    labelStyle?: TextStyle
    testID?: string
  }>
  rowStyle?: StyleProp<ViewStyle>
}

interface InvoicePanelProps {
  invoice: {
    company: {
      name: string
      phone: string
    }
    total_amount: number
    type: string
    invoice_number: string
    invoice_due_date: string
  }
  additionalFields?: Array<{
    label: string
    value: string | number | JSX.Element
    valueStyle?: TextStyle
    labelStyle?: TextStyle
    testID?: string
  }>
  buttons?: Array<{
    label: string
    onPress: () => void
    style?: StyleProp<ViewStyle>
    textStyle?: StyleProp<TextStyle>
    disabled?: boolean
    testID?: string
  }>
  footer?: JSX.Element
  backgroundColor?: string
}

export const InvoicePanel: React.FC<InvoicePanelProps> = ({
  invoice,
  additionalFields = [],
  buttons = [],
  footer,
  backgroundColor,
}) => {
  return (
    <View style={[styles.container, { backgroundColor }]}>
      {/* Company Header Section */}
      <CompanyHeader_v2
        name={invoice.company?.name ?? ''}
        phone={invoice.company?.phone ?? ''}
        totalAmount={Number(invoice.total_amount)}
        style={{
          paddingTop: 20,
          flexDirection: 'row',
          paddingBottom: 14,
        }}
      />

      <Divider style={{ marginBottom: 14 }} />

      {/* Other Invoice Information */}
      <OtherInvoiceInfo_v2
        type={invoice.type}
        invoice_number={invoice.invoice_number}
        invoice_due_date={invoice.invoice_due_date}
      />

      {/* Additional Fields */}
      {additionalFields.length > 0 && (
        <AdditionalInvoiceFields additionalFields={additionalFields} />
      )}

      {/* Footer */}
      {footer && <View style={styles.footer}>{footer}</View>}

      {/* Buttons */}
      <View style={styles.buttonContainer}>
        {buttons.map((button) => (
          <Button
            onPress={button.onPress}
            buttonStyle={[styles.button, button.style]}
            textStyle={button.textStyle}
            disabled={button.disabled}
            testID={button.testID}
            label={button.label}
          />
        ))}
      </View>
    </View>
  )
}

export const AdditionalInvoiceFields: React.FC<AdditionalInvoiceFieldProps> = ({
  additionalFields,
  rowStyle,
}) => {
  return (
    <View>
      {additionalFields.map((field, index) => (
        <React.Fragment key={index}>
          <View style={[styles.fieldRow, rowStyle]}>
            <Text style={[styles.fieldText, field.labelStyle]}>
              {field.label}
            </Text>
            <View style={styles.fieldValueContainer}>
              {typeof field.value === 'string' ||
              typeof field.value === 'number' ? (
                <Text
                  style={[styles.fieldText, styles.valueText, field.valueStyle]}
                >
                  {field.value}
                </Text>
              ) : (
                field.value
              )}
            </View>
          </View>
          <Divider style={{ marginVertical: 14 }} />
        </React.Fragment>
      ))}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingBottom: 8,
    backgroundColor: '#fff',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
    maxWidth: 600,
  },
  fieldRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 14,
  },
  fieldText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '500',
    lineHeight: 21,
    color: '#394D5A',
  },
  valueText: {
    color: '#001929',
  },
  fieldValueContainer: {
    maxWidth: '60%',
    alignItems: 'flex-end',
  },
  buttonContainer: {
    marginTop: 20,
  },
  button: {
    marginBottom: 12,
  },
  footer: {
    marginTop: 20,
    alignItems: 'center',
  },
})
